/**
 * /settings/notifications — Web Push opt-in (Phase 11 / M9).
 *
 * Founder-facing surface to:
 *   - Enable / disable push notifications for the current browser.
 *   - Send a self-test push so they can verify everything is wired.
 *   - See every device that has subscribed (in case they want to
 *     audit / revoke from elsewhere).
 *
 * Visual rules (per ADR-057): monochrome chrome. Status uses the
 * status-color tokens — green for active, yellow for pending, red
 * for blocked / errored. Buttons match the rest of /settings (outline
 * for secondary, default for primary).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, BellOff, BellRing, RefreshCcw, Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { pushApi, pushKeys } from '../../../lib/api/push';
import {
  getSubscriptionStatus,
  type PushStatus,
  PushSubscribeError,
  sendTestPush,
  subscribeToPush,
  unsubscribeFromPush,
} from '../../../lib/push';
import { cn } from '../../../lib/utils';

type FlashTone = 'success' | 'error';
interface Flash {
  tone: FlashTone;
  message: string;
}

function statusBadge(status: PushStatus): { label: string; cls: string; tKey: string } {
  if (!status.supported) {
    return {
      tKey: 'settings.notifications.status_unsupported',
      label: 'Not supported',
      cls: 'border-border bg-muted text-muted-foreground',
    };
  }
  if (!status.vapidConfigured) {
    return {
      tKey: 'settings.notifications.status_unconfigured',
      label: 'Not configured',
      cls: 'border-border bg-muted text-muted-foreground',
    };
  }
  if (status.permission === 'denied') {
    return {
      tKey: 'settings.notifications.status_blocked',
      label: 'Blocked',
      cls: 'border-destructive/40 bg-destructive/10 text-destructive',
    };
  }
  if (status.subscribed) {
    return {
      tKey: 'settings.notifications.status_active',
      label: 'Active',
      cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    };
  }
  return {
    tKey: 'settings.notifications.status_off',
    label: 'Off',
    cls: 'border-border bg-muted text-muted-foreground',
  };
}

function NotificationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [flash, setFlash] = useState<Flash | null>(null);
  const [browserStatus, setBrowserStatus] = useState<PushStatus | null>(null);

  // Read the browser-local PushManager state on mount + after every
  // subscribe / unsubscribe. Memoised so useEffect's dependency check
  // is satisfied without re-firing on every render.
  const refreshBrowserStatus = useCallback(async (): Promise<void> => {
    const s = await getSubscriptionStatus();
    setBrowserStatus(s);
  }, []);
  useEffect(() => {
    void refreshBrowserStatus();
  }, [refreshBrowserStatus]);

  const subscriptionsQ = useQuery({
    queryKey: pushKeys.list(),
    queryFn: pushApi.listSubscriptions,
    refetchOnWindowFocus: true,
  });

  const subscribeM = useMutation({
    mutationFn: subscribeToPush,
    onSuccess: async () => {
      await refreshBrowserStatus();
      await qc.invalidateQueries({ queryKey: pushKeys.list() });
      setFlash(null);
    },
    onError: (err: unknown) => {
      if (err instanceof PushSubscribeError) {
        const messageKey =
          err.code === 'permission_denied'
            ? 'settings.notifications.permission_denied'
            : err.code === 'vapid_missing'
              ? 'settings.notifications.vapid_missing'
              : 'settings.notifications.subscribe_failed';
        setFlash({ tone: 'error', message: t(messageKey) });
      } else {
        setFlash({
          tone: 'error',
          message: t('settings.notifications.subscribe_failed'),
        });
      }
    },
  });

  const unsubscribeM = useMutation({
    mutationFn: unsubscribeFromPush,
    onSuccess: async () => {
      await refreshBrowserStatus();
      await qc.invalidateQueries({ queryKey: pushKeys.list() });
      setFlash(null);
    },
  });

  const testM = useMutation({
    mutationFn: sendTestPush,
    onSuccess: () => {
      setFlash({ tone: 'success', message: t('settings.notifications.test_success') });
    },
    onError: () => {
      setFlash({ tone: 'error', message: t('settings.notifications.test_failed') });
    },
  });

  const badge = browserStatus ? statusBadge(browserStatus) : null;
  const isSubscribed = !!browserStatus?.subscribed;
  const canSubscribe =
    !!browserStatus &&
    browserStatus.supported &&
    browserStatus.vapidConfigured &&
    browserStatus.permission !== 'denied';

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-6 py-6">
      <header>
        <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('settings.notifications.title')}
        </h1>
        <p className="mt-1 text-[12px] text-muted-foreground">
          {t('settings.notifications.subtitle')}
        </p>
      </header>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div className="flex flex-col gap-0.5">
            <CardTitle className="text-[13px] font-semibold">
              {t('settings.notifications.section_browser')}
            </CardTitle>
            <CardDescription className="text-[12px]">
              {t('settings.notifications.subtitle')}
            </CardDescription>
          </div>
          {badge && (
            <span
              className={cn(
                'inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium',
                badge.cls,
              )}
            >
              {t(badge.tKey)}
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {browserStatus && !browserStatus.supported && (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
              {t('settings.notifications.status_unsupported')}
            </p>
          )}
          {browserStatus?.supported && !browserStatus.vapidConfigured && (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
              {t('settings.notifications.vapid_missing')}
            </p>
          )}
          {flash && (
            <p
              role={flash.tone === 'error' ? 'alert' : 'status'}
              className={cn(
                'flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]',
                flash.tone === 'error'
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
              )}
            >
              {flash.tone === 'error' && (
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5" aria-hidden />
              )}
              <span>{flash.message}</span>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {isSubscribed ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => unsubscribeM.mutate()}
                disabled={unsubscribeM.isPending}
              >
                <BellOff aria-hidden />
                {unsubscribeM.isPending
                  ? t('settings.notifications.unsubscribing')
                  : t('settings.notifications.unsubscribe')}
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                onClick={() => subscribeM.mutate()}
                disabled={!canSubscribe || subscribeM.isPending}
              >
                <BellRing aria-hidden />
                {subscribeM.isPending
                  ? t('settings.notifications.subscribing')
                  : t('settings.notifications.subscribe')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => testM.mutate()}
              disabled={!isSubscribed || testM.isPending}
            >
              <Send aria-hidden />
              {testM.isPending
                ? t('settings.notifications.sending_test')
                : t('settings.notifications.test_push')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void refreshBrowserStatus();
                void qc.invalidateQueries({ queryKey: pushKeys.list() });
              }}
            >
              <RefreshCcw aria-hidden />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-[13px] font-semibold">
            {t('settings.notifications.section_devices')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subscriptionsQ.isLoading && (
            <p className="text-[12px] text-muted-foreground">Loading…</p>
          )}
          {subscriptionsQ.data && subscriptionsQ.data.length === 0 && (
            <p className="text-[12px] text-muted-foreground">
              {t('settings.notifications.no_devices')}
            </p>
          )}
          {subscriptionsQ.data && subscriptionsQ.data.length > 0 && (
            <ul className="flex flex-col divide-y divide-border">
              {subscriptionsQ.data.map((sub) => {
                const isCurrent = browserStatus?.endpoint === sub.endpoint;
                return (
                  <li
                    key={sub.id}
                    className="flex items-start justify-between gap-4 py-3 text-[12px]"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">
                        {sub.userAgent ?? 'unknown browser'}
                      </p>
                      <p className="mt-0.5 truncate text-muted-foreground">{sub.endpoint}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                        {t('settings.notifications.last_seen_label')}:{' '}
                        {new Date(sub.lastSeenAt).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      {isCurrent && (
                        <span className="inline-flex items-center rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                          {t('settings.notifications.current_device')}
                        </span>
                      )}
                      {sub.revoked && (
                        <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          revoked
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/settings/notifications')({
  component: NotificationsPage,
});
